import { useEffect, useState } from 'react';

import { isEmpty } from 'lodash';
import { uniqBy } from 'lodash/fp';

import { User } from '@domain/auth';
import { SITECORE_USER_TYPES } from '@domain/clubs';

import { getBackendLogoutUrl, getWebappLogoutUrl } from '@repositories/auth';

import {
  FEATURE_SITECORE_HEADER,
  FEATURE_SITECORE_HOMEPAGE,
  FEATURE_SITECORE_LANDING_PAGE,
  isEnabled,
} from '@utils/features';

import { useDominoUserAccount } from '@domui-hooks/useDominoUserAccount';
import {
  DOMUI_CLUB_DISTRICT_DATA,
  SITECORE_INDIVIDUAL_TOKEN,
} from '@domui-sitecore/constants';
import { getAllDayLabel } from '@domui-sitecore/sitecoreTranslations';
import { getUserType } from '@domui-use-cases/sitecore/helpers/getUserTypes';
import { constructFullName } from '@domui-utils/constructFullName';
import { getOrderedDistrictsList } from '@domui-utils/districts';
import {
  getClubLeadership,
  getDESOnlyClubs,
  getExecutiveSecrataryClubs,
  getUserClubs,
  processOnlyDESClubs,
  sortClubAffiliations,
} from '@domui-utils/getUserClubs';

import { useTranslation } from '@external/react-i18next';

export const useSiteCoreUserType = (
  user: User | undefined,
  isProcessingLogin: boolean
) => {
  const { t } = useTranslation();
  const individualId = user?.individualId || '';
  const { data, loading } = useDominoUserAccount(individualId);
  const [areClubsAvailable, setClubsAvailable] = useState(false);

  useEffect(() => {
    if (
      individualId &&
      (isEnabled(FEATURE_SITECORE_HEADER) ||
        isEnabled(FEATURE_SITECORE_HOMEPAGE) ||
        isEnabled(FEATURE_SITECORE_LANDING_PAGE))
    ) {
      setClubsAvailable(true);
    }
  }, [individualId]);

  if (
    isEnabled(FEATURE_SITECORE_HEADER) ||
    isEnabled(FEATURE_SITECORE_HOMEPAGE) ||
    isEnabled(FEATURE_SITECORE_LANDING_PAGE)
  ) {
    if (!isProcessingLogin && !user?.isLoggedIn) {
      window.localStorage.setItem(SITECORE_INDIVIDUAL_TOKEN, '');
      window.localStorage.setItem(DOMUI_CLUB_DISTRICT_DATA, '');

      return SITECORE_USER_TYPES.UNAUTHENTICATED;
    }

    if (loading) {
      window.localStorage.setItem(SITECORE_INDIVIDUAL_TOKEN, '');
      return SITECORE_USER_TYPES.USER_LOADING;
    }
    const clubAffiliations = data?.getClubDistrictData?.organizations || [];
    const userClubsForClubsMenuItems = getUserClubs(clubAffiliations);
    const userClubs = getClubLeadership(userClubsForClubsMenuItems || [], t);

    // Retrieve MDES/CES Clubs
    const ESClubsForClubsMenuItems = getExecutiveSecrataryClubs(
      clubAffiliations
    );
    const executiveSecrataryClubs = getClubLeadership(
      ESClubsForClubsMenuItems || [],
      t
    );

    // Retrieve DES Clubs
    const DESDistrict = getDESOnlyClubs(clubAffiliations);
    const DESClubsForClubsMenuItems = processOnlyDESClubs(
      clubAffiliations,
      DESDistrict
    );
    const DESClubs = getClubLeadership(DESClubsForClubsMenuItems || [], t);

    // Retrieve Districts
    const userClubsForDistrictsMenuItems = getUserClubs(clubAffiliations, true);
    const districtsInfo = getOrderedDistrictsList(
      userClubsForDistrictsMenuItems
    );

    const destination = window.location.pathname;
    const newUser =
      !individualId &&
      (destination.endsWith('/account/select-member-status') ||
        destination.endsWith('/account/map-by-email') ||
        destination.endsWith('/contact'));
    const existingUser =
      areClubsAvailable && !isProcessingLogin && user?.isLoggedIn;

    let userType = SITECORE_USER_TYPES.USER_LOADING;
    if (newUser) {
      userType = SITECORE_USER_TYPES.UNAUTHENTICATED;
    } else if (existingUser) {
      const isDESInfoAvailable =
        !isEmpty(executiveSecrataryClubs) || !isEmpty(DESDistrict);
      userType = getUserType(clubAffiliations, isDESInfoAvailable);
    }

    const allClubsTogether = [
      ...userClubs,
      ...executiveSecrataryClubs,
      ...DESClubs,
    ];
    const sortedClubs = sortClubAffiliations(allClubsTogether);

    const clubs = uniqBy(
      'riClubId',
      sortedClubs.filter(c => c.riClubId)
    );

    const districts = uniqBy('id', [...districtsInfo]);

    const userTypeTranslation = (userRole: string) => {
      switch (userRole) {
        case SITECORE_USER_TYPES.NON_MEMBER:
          return t('sitecore-non-member-greeting', 'Participant');

        case SITECORE_USER_TYPES.MEMBER:
          return t('sitecore-member-greeting', 'Member');

        case SITECORE_USER_TYPES.LEADER:
          return t('sitecore-leader-greeting', 'Leader');

        default:
          return '';
      }
    };
    const userRole = userTypeTranslation(userType);

    const firstName = data?.individualInfo?.firstName || '';
    const lastName = data?.individualInfo?.lastName || '';
    const middleName = data?.individualInfo?.middleName || '';
    const prefix = data?.individualInfo?.prefix || '';
    const suffix = data?.individualInfo?.suffix || '';
    const fullName = constructFullName(
      prefix,
      firstName,
      middleName,
      lastName,
      suffix
    );

    const backendLogoutUrl = getBackendLogoutUrl();
    const logOutUrl = getWebappLogoutUrl(backendLogoutUrl);
    const weekDayLabels = getAllDayLabel(t);
    const districtLabel = t(
      'search.members.filters-district-label',
      'District'
    );

    const profileInfo = {
      firstName,
      lastName,
      middleName,
      fullName,
      userRole,
    };

    window.localStorage.setItem(
      DOMUI_CLUB_DISTRICT_DATA,
      JSON.stringify({
        individualInfo: data?.individualInfo,
        getClubDistrictData: data?.getClubDistrictData,
        districtLabel,
        clubs,
        districts,
        profileInfo,
        weekDayLabels,
        logOutUrl,
        rotaryYear: data?.rotaryYear,
      })
    );

    return userType;
  }
  return SITECORE_USER_TYPES.UNAUTHENTICATED;
};
